import React, { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, LifeBuoy, NotebookText, ShieldPlus, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { createBcSupportTicket } from "@/api/backgroundCheck";
import { cn } from "@/lib/utils";
import { usePosthog } from "@/hooks/usePosthog";

/* ------------------------------------------------------------------ *
 * Shared chrome for the Verification Checks pages of BOTH personas:
 * the pill section nav + the mobile page heading (desktop title lives
 * in the top bar Header per the design system, §12.7). The persona is
 * derived from the current route.
 * ------------------------------------------------------------------ */

type VcNavItem = { title: string; href: string; icon: LucideIcon; isDashboard?: boolean };

export const VcSectionNav = ({ className }: { className?: string }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { trackEvent } = usePosthog();

  const isEmployer = location.pathname.startsWith("/employer");
  const base = isEmployer ? "/employer/verification-checks" : "/jobseeker/verification-checks";

  // A jobseeker filling an employer-requested application (?bc-request-id=...)
  // is doing tracked work, not shopping — highlight the dashboard there,
  // mirroring the legacy bcEntry behavior.
  const fromEmployerRequest = !isEmployer && Boolean(searchParams.get("bc-request-id"));

  const items: VcNavItem[] = [
    { title: t("verification_checks.nav.pricing"), href: `${base}/pricing`, icon: ShieldPlus },
    {
      title: t(
        isEmployer ? "verification_checks.nav.reports" : "verification_checks.nav.dashboard",
      ),
      href: `${base}/dashboard`,
      icon: NotebookText,
      isDashboard: true,
    },
  ];

  const isActive = (item: VcNavItem) =>
    fromEmployerRequest ? !!item.isDashboard : location.pathname.startsWith(item.href);

  return (
    <nav
      aria-label={t("verification_checks.nav.aria_label")}
      className={cn(
        "-mx-1 flex [scrollbar-width:none] gap-2 overflow-x-auto px-1 pb-1 [&::-webkit-scrollbar]:hidden [&>*]:shrink-0",
        className,
      )}
    >
      {items.map((item) => {
        const active = isActive(item);
        const Icon = item.icon;
        return (
          <button
            key={item.href}
            type="button"
            aria-current={active ? "page" : undefined}
            onClick={() => {
              navigate(item.href);
              trackEvent("Verification Checks: Section Nav Clicked", {
                title: item.title,
                href: item.href,
              });
            }}
            className={cn(
              "inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors",
              active
                ? "border-brand-accent-border bg-brand-accent text-primary"
                : "border-input text-muted-foreground hover:text-primary bg-white hover:bg-blue-100",
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="whitespace-nowrap">{item.title}</span>
          </button>
        );
      })}
    </nav>
  );
};

export const VcPageHeader = ({
  title,
  subtitle,
  actions,
  className,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("space-y-4", className)}>
    <div className="space-y-1 md:hidden">
      <h1 className="text-foreground text-xl font-bold tracking-tight sm:text-2xl">{title}</h1>
      {subtitle && <p className="text-muted-foreground text-sm">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <VcSectionNav className="min-w-0 flex-1" />
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  </div>
);

/**
 * One-click support for a failed check. Tries the ticket endpoint
 * (SUPPORT_TICKETS_API.md); until the backend ships it, falls back to the
 * Contact Us form with the same message prefilled, one click from Send.
 */
export const BcSupportButton = ({
  application,
  className,
}: {
  application: { id: string; created_at?: string; package?: { name?: string } };
  className?: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { trackEvent } = usePosthog();
  const [notified, setNotified] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notified || submitting) return;
    trackEvent("BC Failed: Contact Support Clicked", { applicationId: application.id });
    const message = t("bc_status.support_message", {
      package: application.package?.name,
      id: application.id,
      date: application?.created_at ? new Date(application.created_at).toLocaleDateString() : "",
    });
    setSubmitting(true);
    try {
      await createBcSupportTicket(application.id, message);
      setNotified(true);
      toast({
        title: t("bc_status.support_ticket.success_title"),
        description: t("bc_status.support_ticket.success_description"),
      });
      trackEvent("BC Failed: Support Ticket Created", { applicationId: application.id });
    } catch {
      navigate("/contact-us", { state: { message } });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Button
      variant="outline-soft"
      size="sm"
      disabled={notified}
      loading={submitting}
      className={cn("h-9 rounded-lg", className)}
      onClick={handleClick}
    >
      {notified ? (
        <>
          <Check className="mr-1.5 h-4 w-4 text-[#047857]" />
          {t("bc_status.support_ticket.notified")}
        </>
      ) : (
        <>
          <LifeBuoy className="mr-1.5 h-4 w-4" />
          {t("bc_status.action.contact_support")}
        </>
      )}
    </Button>
  );
};

/* ------------------------------------------------------------------ *
 * Read-only display primitives shared by the submitted-application
 * views (jobseeker preview + employer review).
 * ------------------------------------------------------------------ */

export const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => {
  if (value == null || value === "") return null;
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground text-xs font-medium">{label}</p>
      <p className="text-foreground mt-0.5 truncate text-sm font-medium">{value}</p>
    </div>
  );
};

export const PreviewSection = ({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) => (
  <section className="border-border bg-card rounded-lg border p-5 shadow-xs sm:p-6">
    <div className="flex items-center gap-2.5">
      <span className="bg-brand-accent text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
        <Icon className="h-4 w-4" />
      </span>
      <h2 className="text-foreground text-base font-semibold sm:text-lg">{title}</h2>
    </div>
    <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
  </section>
);

export const maskSsn = (ssn?: string) => (ssn ? `***-**-${ssn.slice(-4)}` : "");
